import React from "react";
import { consolidationRequest as api } from "../api/consolidationApi.js";
import { readOrderDataRevision } from "../utils/orderDataSync.js";
import { forgetConsolidationDay, lastConsolidationDay, rememberConsolidationDay } from "../utils/consolidationViewStorage.js";
import useOrderDataSync from "./useOrderDataSync.js";
import { holdGeotabDays } from "./useGeotabDay.js";

// Périodes déjà vues sur la page : réaffichées aussitôt au retour, puis rechargées.
export function rememberPeriod(cache, key, value, limit = 3) {
  cache.delete(key);
  cache.set(key, value);
  if (cache.size > limit) cache.delete(cache.keys().next().value);
}

export default function useConsolidationData(from, to, busy, enabled = true, path = "") {
  // Une lecture allégée (path) a sa propre mémoire : jamais réaffichée comme journée complète.
  const period = `${path}${from}:${to}`;
  // Au retour sur la page, la dernière journée lue s'affiche dès le premier rendu, sans écran de chargement.
  // Les données gardent leur lecture : en changeant d'onglet sur les mêmes dates, celles de l'autre ne s'affichent jamais.
  const [loaded, setLoaded] = React.useState(() => ({ period, data: enabled ? lastConsolidationDay(period) : null }));
  const data = loaded.period === period ? loaded.data : null;
  // Vrai tant que les données affichées viennent de la mémoire et attendent leur rechargement.
  const [stale, setStale] = React.useState(() => Boolean(loaded.data));
  const [error, setError] = React.useState(null);
  const [options, setOptions] = React.useState({ employees: [], trucks: [], mills: [] });
  const active = React.useRef(null);
  const seen = React.useRef(new Map());
  const currentPeriod = React.useRef(period);
  currentPeriod.current = period;
  const load = React.useCallback(async () => {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    const revision = readOrderDataRevision();
    // Ouverture, retour, rechargement, sonde : Geotab attend la fin de toute lecture de la journée.
    const releaseGeotab = holdGeotabDays(from, to);
    try {
      const result = await api(`${path}?${new URLSearchParams({ from_date: from, to_date: to })}`, { signal: controller.signal });
      if (controller.signal.aborted || currentPeriod.current !== period || revision !== readOrderDataRevision()) return null;
      rememberPeriod(seen.current, period, result);
      if (from === to && !path) rememberConsolidationDay(period, result);
      setStale(false);
      setLoaded(previous => ({ period, data: { ...result, days: result.days.map(day => {
        const old = previous.period === period ? previous.data?.days.find(candidate => candidate.date === day.date) : null;
        if (day.consolidation?.editable !== false || day.consolidation?.has_photo || !old?.driver_groups?.length) return day;
        const readonly = route => ({ ...route, sheet: { ...route.sheet, day_editable: false } });
        return { ...day, retained_drafts: true, driver_groups: old.driver_groups.map(group => ({ ...group, routes: group.routes.map(readonly) })),
          unassigned_routes: old.unassigned_routes.map(readonly), preloads: old.preloads?.map(row => ({ ...row, day_editable: false })),
          evening_preloads: old.evening_preloads, preload_evening_days: old.preload_evening_days };
      }) } })); setError(null);
      return result;
    } catch (error) {
      if (controller.signal.aborted || currentPeriod.current !== period || revision !== readOrderDataRevision()) return null;
      throw error;
    } finally {
      releaseGeotab();
    }
  }, [from, to, period, path]);
  React.useEffect(() => {
    if (!enabled) return undefined;
    const kept = seen.current.get(period) || lastConsolidationDay(period);
    setLoaded({ period, data: kept }); setStale(Boolean(kept)); setError(null);
    void load().catch(setError);
    return () => active.current?.abort();
  }, [enabled, load, period]);
  React.useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    api("/options", { signal: controller.signal }).then(setOptions).catch((error) => {
      if (!controller.signal.aborted) setError(error);
    });
    return () => controller.abort();
  }, [enabled]);
  useOrderDataSync({ source: "consolidation", enabled: enabled && !busy, refresh: load, onError: setError });
  React.useEffect(() => {
    if (!enabled || busy || !(data?.sync_revision || data?.departure_revision)) return undefined;
    const controller = new AbortController();
    let checking = false;
    const probe = async () => {
      if (checking || document.visibilityState === "hidden") return;
      checking = true;
      try {
        const result = await api(`/${data?.sync_revision ? "revision" : "departure-revision"}?${new URLSearchParams({ from_date: from, to_date: to })}`, { signal: controller.signal });
        if (!controller.signal.aborted && result.revision !== (data.sync_revision || data.departure_revision)) await load();
      } catch { /* A transient probe failure is retried without replacing editor drafts. */ }
      finally { checking = false; }
    };
    const timer = window.setInterval(probe, 15000);
    window.addEventListener("focus", probe);
    document.addEventListener("visibilitychange", probe);
    return () => { controller.abort(); window.clearInterval(timer); window.removeEventListener("focus", probe); document.removeEventListener("visibilitychange", probe); };
  }, [enabled, busy, (data?.sync_revision || data?.departure_revision), from, to, load]);
  const updatePreloadSettings = settings => setOptions(previous => ({ ...previous, preload_settings: settings }));
  return { data, stale, error, setError, options, updatePreloadSettings, load, clear: () => { seen.current.clear(); forgetConsolidationDay(); setLoaded({ period, data: null }); } };
}
